import { ContactAPI } from '@business/api/contact';
import { EmailAPI } from '@business/api/email';
import { ContactActivityPageQuery, ContactActivityRequest, ContactActivityResponse } from '@business/entities/contact';
import { PreviewEmailRequest, SendEmailRequest, SendTestEmailRequest } from '@business/entities/gmail_messages';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { R } from '@core/utils/r';
import { Repository } from '@core/utils/repository';
import { pipeIf } from '@core/utils/rxjs/operators';
import { showDeleteConfirmation } from '@modules/common';
import { map, switchMap } from 'rxjs/operators';

export class ActivityBloc extends BaseBloc {
  private readonly activityRepo = new Repository<ContactActivityResponse>({
    getItemId: activty => activty.id,
    pagenated: true,
  });

  readonly activities$ = this.activityRepo.items$.pipe(
    map(items => R.orderBy(items, item => [item.pinnedAt || 0, item.createdAt], ['desc', 'desc'])),
  );

  onReset() {
    this.activityRepo.reset();
  }

  fetchPaginateActivities = (options: ContactActivityPageQuery) => {
    return ContactAPI.fetchPaginateActivities(options);
  };

  fetchActivities = (options: ContactActivityPageQuery) => {
    if (options.page === 1) {
      this.activityRepo.reset();
    }
    return ContactAPI.fetchActivities(options).pipe(
      pipeIf((options.page === 1 || options.page === undefined) && this.activityRepo.ops.reset()),
      pipeIf(options.page !== undefined && options.page > 1 && this.activityRepo.ops.upsertMany(item => ({ item }))),
      //   this.activityRepo.ops.upsertMany(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_activities_error') }),
    );
  };

  createActivity = (data: ContactActivityRequest, showMessage = true) => {
    return ContactAPI.createActivity(data).pipe(
      this.activityRepo.ops.upsertOne(item => ({ item })),
      pipeIf(
        showMessage &&
          handleMessage({
            type: requestMessage('create_activity'),
          }),
      ),
    );
  };

  updateActivity = (id: string, data: Partial<ContactActivityRequest>) => {
    return ContactAPI.updateActivity(id, data).pipe(
      this.activityRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_activity'),
      }),
    );
  };

  onTogglePin = (id: string) => {
    return ContactAPI.onTogglePin(id).pipe(
      this.activityRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_activity'),
      }),
    );
  };

  onTogglePinEmail = (id: string) => {
    return ContactAPI.onTogglePinEmail(id).pipe(
      handleMessage({
        type: requestMessage('update_activity'),
      }),
    );
  };

  deleteActivity = (id: string) => {
    return showDeleteConfirmation('Delete Activity', 'Do you really want to remove this activity?').pipe(
      switchMap(() => ContactAPI.deleteActivity(id)),
      this.activityRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_activity'),
      }),
    );
  };

  sendEmail = (data: SendEmailRequest) => {
    return EmailAPI.sendEmail(data).pipe(
      this.activityRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('send_email'),
      }),
    );
  };

  previewEmail = (data: PreviewEmailRequest) => {
    return EmailAPI.previewEmail(data).pipe(
      handleMessage({
        error: requestMessage('preview_email_error'),
      }),
    );
  };

  sendTestEmail = (data: SendTestEmailRequest) => {
    return EmailAPI.sendTestEmail(data).pipe(
      handleMessage({
        type: requestMessage('send_test_email'),
      }),
    );
  };
}
